import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateChildFn,
  CanActivateFn,
  Router,
  RouterStateSnapshot
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { PermissionsService } from '../../permissions/permissions.service';
import { PermissionsDto } from '../../permissions/interfaces/permissions.interface';
import { ResponsePermissionsInterface } from '../../permissions/interfaces/response-permissions.interface';
import { Role } from '../../permissions/roles.enum';
import { AuthService } from '../auth.service';

export const canActivate: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean> => {
  const permissionsService = inject(PermissionsService);
  const router = inject(Router);
  const authService = inject(AuthService);
  const module = route.data['module'] as keyof PermissionsDto;
  const redirectNextModuleUrl = route.data['redirectNextModuleUrl'] || '/settings';

  // Allow demo mode users to access all routes without permission checks
  if (authService.isDemoMode()) {
    return of(true);
  }

  if (!module) {
    return of(true);
  }

  return permissionsService.fetchPermissions().pipe(
    map((data: ResponsePermissionsInterface) => {
      const { role, permissions, status } = data;
      if (role === Role.SHOP_OWNER || role === Role.SHOP_SPY) {
        return true;
      }

      if (
        (role === Role.SHOP_COLLABORATOR && !permissions) ||
        (role === Role.SHOP_COLLABORATOR && permissions === null) ||
        status === 'NO_ACCESS'
      ) {
        if (!state.url.includes('/settings')) {
          router.navigate(['/settings']);
        }
        return state.url.includes('/settings');
      }

      if (permissions) {
        const hasPermission = checkPermission(permissions, module);
        if (!hasPermission) {
          // Prevent infinite redirect loop
          if (!state.url.includes(redirectNextModuleUrl)) {
            router.navigate([redirectNextModuleUrl]);
          }
        }
        return hasPermission;
      }

      router.navigate(['/login']);
      return false;
    }),
    catchError(error => {
      console.error('Error in permissions guard:', error);

      if (state.url.includes('/settings')) {
        return of(true);
      }

      router.navigate(['/settings']);
      return of(false);
    })
  );
};

function checkPermission(permissions: PermissionsDto, module: keyof PermissionsDto): boolean {
  const modulePermission = permissions[module];

  return !(!modulePermission || modulePermission.none);
}

export const permissionsGuard: CanActivateChildFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => canActivate(route, state);
